import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageTemplateCreateSchema } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input, Textarea } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';
import { TtsVoiceSelect } from '../components/TtsVoiceSelect.js';
import { EmailBodyEditor } from '../components/email-body-editor.js';
import { VariablePicker } from '../components/variable-picker.js';

interface FormState {
  name: string;
  tts_voice: string;
  intro_voice_message: string;
  closing_voice_message: string;
  confirm_identity: boolean;
  sms_body: string;
  email_subject: string;
  email_body: string;
}

const EMPTY: FormState = {
  name: '',
  tts_voice: '',
  intro_voice_message: '',
  closing_voice_message: '',
  confirm_identity: false,
  sms_body: '',
  email_subject: '',
  email_body: '',
};

interface MessageTemplate {
  id: string;
  name: string;
  tts_voice?: string;
  intro_voice_message?: string;
  closing_voice_message?: string;
  confirm_identity: boolean;
  sms_body?: string;
  email_subject?: string;
  email_body?: string;
}

export function EditMessageTemplatePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const templateQ = useQuery({
    queryKey: ['message-template', id],
    queryFn: () => api.get(`/v1/message-templates/${id}`) as Promise<MessageTemplate>,
    enabled: !!id,
  });

  useEffect(() => {
    if (!templateQ.data) return;
    const t = templateQ.data;
    setForm({
      name: t.name ?? '',
      tts_voice: t.tts_voice ?? '',
      intro_voice_message: t.intro_voice_message ?? '',
      closing_voice_message: t.closing_voice_message ?? '',
      confirm_identity: !!t.confirm_identity,
      sms_body: t.sms_body ?? '',
      email_subject: t.email_subject ?? '',
      email_body: t.email_body ?? '',
    });
  }, [templateQ.data]);

  const save = useMutation({
    mutationFn: (payload: unknown) => api.put(`/v1/message-templates/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['message-templates'] });
      qc.invalidateQueries({ queryKey: ['message-template', id] });
      navigate('/message-templates');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBannerError(null);

    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      confirm_identity: form.confirm_identity,
    };
    for (const k of [
      'tts_voice',
      'intro_voice_message',
      'closing_voice_message',
      'sms_body',
      'email_subject',
      'email_body',
    ] as const) {
      const v = form[k].trim();
      if (v) payload[k] = v;
    }

    const fieldIssues = fieldErrors(MessageTemplateCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    save.mutate(payload);
  };

  const update = <K extends keyof FormState>(key: K) => (value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  if (templateQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (templateQ.error) return <FormBanner message={extractDetail(templateQ.error)} />;

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Edit message template</h1>
        <p className="text-sm text-ink-muted">
          Changes apply to future deliveries. In-flight events / campaigns already snapshotted
          their content at scheduling time.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Template</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Name" required error={errors?.['name']}>
              {(id) => (
                <Input
                  id={id}
                  value={form.name}
                  onChange={(e) => update('name')(e.target.value)}
                  required
                  autoFocus
                />
              )}
            </FormField>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Voice</legend>
              <FormField
                label="TTS voice"
                hint="Voice used for text-to-speech generation. Leave blank to inherit the customer default."
                error={errors?.['tts_voice']}
              >
                {(id) => (
                  <TtsVoiceSelect
                    id={id}
                    value={form.tts_voice}
                    onChange={update('tts_voice')}
                    className="block w-full rounded border border-border bg-surface px-3 py-2 text-ink"
                    includeEmpty
                  />
                )}
              </FormField>
              <FormField label="Intro message" error={errors?.['intro_voice_message']}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={2}
                    value={form.intro_voice_message}
                    onChange={(e) => update('intro_voice_message')(e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Closing message" error={errors?.['closing_voice_message']}>
                {(id) => (
                  <Textarea
                    id={id}
                    rows={2}
                    value={form.closing_voice_message}
                    onChange={(e) => update('closing_voice_message')(e.target.value)}
                  />
                )}
              </FormField>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={form.confirm_identity}
                  onChange={(e) => update('confirm_identity')(e.target.checked)}
                />
                Ask the recipient to confirm identity before message playback
              </label>
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">SMS</legend>
              <FormField
                label="SMS body"
                hint="Max 1600 chars (10 SMS segments). Variables interpolated."
                error={errors?.['sms_body']}
              >
                {(id) => (
                  <div className="space-y-2">
                    <VariablePicker
                      onInsert={(token) =>
                        update('sms_body')(form.sms_body + token)
                      }
                    />
                    <Textarea
                      id={id}
                      rows={3}
                      maxLength={1600}
                      value={form.sms_body}
                      onChange={(e) => update('sms_body')(e.target.value)}
                    />
                  </div>
                )}
              </FormField>
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Email</legend>
              <FormField label="Subject" error={errors?.['email_subject']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.email_subject}
                    onChange={(e) => update('email_subject')(e.target.value)}
                  />
                )}
              </FormField>
              <FormField label="Body" error={errors?.['email_body']}>
                {() => (
                  <EmailBodyEditor
                    value={form.email_body}
                    onChange={update('email_body')}
                    placeholder="Compose the email body. Use the Insert variable picker to add tokens like {{first_name}}."
                  />
                )}
              </FormField>
            </fieldset>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/message-templates')}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
